import * as amplitude from "@amplitude/analytics-browser";
import type { Types } from "@amplitude/analytics-browser";
import { sessionReplayPlugin } from "@amplitude/plugin-session-replay-browser";
import { format } from "date-fns";
import { getDeviceType } from "../functions/utility";
import { getBrowser } from "../functions/utility";
import { getDashboardEnvironment } from "../functions/client-code";

export interface Auth {
  client: { client_code: string };
  user: { role: string; _id: string };
}

/**
 * Silences Amplitude's console logging outside local dev. Gates `console.*`
 * only — `logLevel` never reaches the transport/queue/retry paths, so event
 * delivery is unaffected. Needed because Sentry's `captureConsoleIntegration`
 * turns each SDK error into an issue that can trigger an error-replay upload.
 *
 * Literals, not `amplitude.Types.LogLevel`: this is evaluated at module load
 * and the Redux store imports this file, so a *runtime* namespace read throws
 * wherever the SDK is stubbed (`src/test/setup.ts`). The type-only import is
 * erased at build time.
 */
const LOG_LEVEL_WARN = 2 as Types.LogLevel; // Error + Warn
const LOG_LEVEL_NONE = 0 as Types.LogLevel; // silent

export const AMPLITUDE_LOG_LEVEL =
  getDashboardEnvironment() === "development" ? LOG_LEVEL_WARN : LOG_LEVEL_NONE;

const MODA_CLIENT_CODE = "moda";
const modaAmplitude = amplitude.createInstance();
let isModaTrackingEnabled = false;

export const initModaTracking = (clientCode: string | null | undefined) => {
  if (isModaTrackingEnabled) return;
  if (clientCode !== MODA_CLIENT_CODE) return;

  const modaApiKey = import.meta.env.VITE_AMPLITUDE_API_KEY_MODA;
  if (!modaApiKey) return;

  modaAmplitude.init(modaApiKey, {
    logLevel: AMPLITUDE_LOG_LEVEL,
    autocapture: {
      pageViews: {
        eventType: "page_viewed",
      },
    },
    defaultTracking: {
      sessions: true,
    },
  });

  modaAmplitude.add(
    sessionReplayPlugin({
      sampleRate: 1,
    })
  );

  isModaTrackingEnabled = true;
};

interface UserLoginData {
  user_id: string;
  email: string;
  role: string;
  org_id?: string;
  org_name?: string;
  team_id?: string;
  name?: string;
  plan?: string;
}

interface EventProperties {
  [key: string]: string | number | boolean | object;
}

export const setUserDetailsOnLogin = (
  userData: UserLoginData,
  eventName: string
) => {
  const identifyObj = new amplitude.Identify();

  // Set user ID. Guarded: setUserId(undefined) force-clears the identity in the SDK
  // (browser-client `userId === undefined` branch), so a missing id must be a no-op
  // rather than a wipe of the persisted user.
  if (userData?.user_id) {
    amplitude?.setUserId(userData.user_id);
  }

  // Set user properties
  identifyObj?.set("name", userData?.name || "unknown");
  identifyObj?.set("email", userData?.email || "unknown");
  identifyObj?.set("role", userData?.role || "unknown");
  identifyObj?.set("environment", getDashboardEnvironment());
  if (userData?.org_id) {
    identifyObj?.set("org_id", userData?.org_id);
  }
  if (userData?.org_name) {
    identifyObj?.set("org_name", userData?.org_name);
  }
  if (userData?.team_id) {
    identifyObj?.set("team_id", userData?.team_id);
  }

  amplitude?.identify(identifyObj);

  // Set group properties
  if (userData?.org_id) {
    amplitude?.setGroup("org_id", userData.org_id);
  }
  if (userData?.role) {
    amplitude?.setGroup("role", userData.role);
  }
  if (userData?.team_id) {
    amplitude?.setGroup("team_id", userData.team_id);
  }

  amplitude?.track(eventName, {
    user_id: userData?.user_id,
    email: userData?.email,
    role: userData?.role,
    org_id: userData?.org_id || "unknown",
    org_name: userData?.org_name || "Unknown Organization",
    team_id: userData?.team_id || "unknown",
    login_method: "email_password",
  });

  if (isModaTrackingEnabled) {
    const modaIdentifyObj = new amplitude.Identify();

    // Same guard as the primary instance — otherwise the two projects' identities
    // diverge permanently on the first refresh.
    if (userData?.user_id) {
      modaAmplitude.setUserId(userData.user_id);
    }

    modaIdentifyObj.set("name", userData?.name || "unknown");
    modaIdentifyObj.set("email", userData?.email || "unknown");
    modaIdentifyObj.set("role", userData?.role || "unknown");
    modaIdentifyObj.set("environment", getDashboardEnvironment());
    if (userData?.org_id) {
      modaIdentifyObj.set("org_id", userData?.org_id);
    }
    if (userData?.org_name) {
      modaIdentifyObj.set("org_name", userData?.org_name);
    }
    if (userData?.team_id) {
      modaIdentifyObj.set("team_id", userData?.team_id);
    }

    modaAmplitude.identify(modaIdentifyObj);

    if (userData?.org_id) {
      modaAmplitude.setGroup("org_id", userData.org_id);
    }
    if (userData?.role) {
      modaAmplitude.setGroup("role", userData.role);
    }
    if (userData?.team_id) {
      modaAmplitude.setGroup("team_id", userData.team_id);
    }

    modaAmplitude.track(eventName, {
      user_id: userData?.user_id,
      email: userData?.email,
      role: userData?.role,
      org_id: userData?.org_id || "unknown",
      org_name: userData?.org_name || "Unknown Organization",
      team_id: userData?.team_id || "unknown",
      login_method: "email_password",
    });
  }
};

export const trackSessionEnded = (auth: Auth) => {
  const device = getDeviceType();
  const browser = getBrowser();
  trackEvents("session_ended", {
    org_id: auth?.client?.client_code || "unknown",
    role: auth?.user?.role || "unknown",
    device_type: device,
    browser: browser,
    logout_method: "manual",
    user_id: auth?.user?._id || "unknown",
    location: window.location.href,
  });
};

export const trackEvents = (eventName: string, properties: EventProperties) => {
  const eventProperties = {
    Date: format(new Date(), "dd/MM/yyyy, HH:mm:ss"),
    ...properties,
  };

  amplitude?.track(eventName, eventProperties);

  if (isModaTrackingEnabled) {
    modaAmplitude.track(eventName, eventProperties);
  }
};

export { amplitude };
